import React, { useCallback, useContext, useEffect, useState } from 'react';
import { defineMessages } from 'react-intl';

import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner/spinner';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';

import type { Doc } from '../../../hooks/useUploadReducer.ts';
import { DocErrors, ErrorRoles } from '../../../hooks/useUploadReducer.ts';
import { ImportStateContext } from '../../ImportStateContextProvider.tsx';
import { i18n as uploadErrorMessages } from '../../../hooks/useShowUploadErrors.tsx';

import { ProgressPercentage, ProgressTextBulk } from './ProgressModal.tsx';
import { ProgressStatus } from './progress-types.ts';
// eslint-disable-next-line import/no-cycle
import { ProgressMessage } from './ProgressMessage.tsx';
import { ProgressBarItem } from './ProgressBarItem.tsx';

const styles = cssMap({
	progress: {
		maxHeight: '180px',
		paddingRight: token('space.200'),
		overflowY: 'auto',
		scrollbarWidth: 'thin',
	},
});

export const i18n: {
	uploadModalProgressTitleBulk: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	uploadModalProgressTitleBulkFailed: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	uploadModalProgressTitleBulk: {
		id: 'import-from-sources.upload-modal.progressTitleBulk',
		defaultMessage: 'Importing documents…',
		description: 'Status message when importing is in progress',
	},
	uploadModalProgressTitleBulkFailed: {
		id: 'import-from-sources.upload-modal.uploadModalProgressTitleBulkFailed',
		defaultMessage: 'Bulk import failed!',
		description:
			'The text is shown as a heading in the bulk import progress modal when the entire bulk import operation has failed. It indicates to the user that no documents were successfully imported.',
	},
});

type ProgressBulkDocModalProps = {
	status: ProgressStatus;
	progress: number;
	docs: Doc[];
	bulkParentPageId: string | null;
	isCancelledDocumentsDeleted: boolean;
	/**
	 * Collapses the per-file progress bars into a single aggregate bar.
	 * Enabled for the Import in Onboarding V2 experiment (TWCG-2304 / SPICE-2553)
	 * so a multi-file import reads as one operation rather than a stack of loaders.
	 */
	useSingleProgressBar?: boolean;
};

export const ProgressBulkDocModal: React.FC<ProgressBulkDocModalProps> = ({
	status,
	progress,
	docs,
	bulkParentPageId,
	isCancelledDocumentsDeleted,
	useSingleProgressBar = false,
}) => {
	const {
		docActions: { uploadDocFailure },
	} = useContext(ImportStateContext);
	const getProgressForOneDoc = (completed: boolean, i: number): number => {
		let result = !completed ? progress - (i + 1) : 100;
		if (result < 0) result = 0;
		return result;
	};
	// eslint-disable-next-line
	const getCommonProgress = (): number => {
		if (status === ProgressStatus.SUCCESS) return 100;
		let result = 0;
		for (let i = 0; i < docs.length; i++) {
			result += docs[i].completed ? 100 : progress - (i + 1);
		}
		return result < 0 ? 1 : Math.round(result / docs.length);
	};

	const [isDocWasCancelled, setIsDocWasCancelled] = useState(false);
	const [isDeleteSingleDocStatus, setIsDeleteSingleDocStatus] = useState(false);

	const handleClick = useCallback(
		(doc: any) => {
			setIsDocWasCancelled(true);
			if (doc?.controller && !bulkParentPageId) {
				doc.controller.abort();
			}
			uploadDocFailure(doc.document?.name, {
				type: DocErrors.REQUEST_ABORTED_ERROR,
				role: ErrorRoles.ERROR,
			});
		},
		[uploadDocFailure, bulkParentPageId],
	);

	useEffect(() => {
		if (isDocWasCancelled && status !== ProgressStatus.ERROR && getCommonProgress() === 100) {
			return setIsDeleteSingleDocStatus(true);
		}
		return setIsDeleteSingleDocStatus(false);
	}, [status, isDocWasCancelled, getCommonProgress]); //TODO: remove getCommonProgress from useEffect dependencies

	const getProgressMessage = () => {
		if (isDeleteSingleDocStatus) {
			return uploadErrorMessages.operationAborted;
		}
		return status !== ProgressStatus.ERROR
			? i18n.uploadModalProgressTitleBulk
			: i18n.uploadModalProgressTitleBulkFailed;
	};

	return (
		<>
			<ProgressTextBulk>
				<ProgressMessage status={status} message={getProgressMessage()} />
				<ProgressPercentage data-testid="progress-percentage">
					{status !== ProgressStatus.ERROR && !isDeleteSingleDocStatus && `${getCommonProgress()}%`}
				</ProgressPercentage>
				{isDeleteSingleDocStatus && !isCancelledDocumentsDeleted && (
					<Spinner testId="progress-bulk-doc-modal-spinner" />
				)}
			</ProgressTextBulk>
			<Box xcss={styles.progress} testId="progress-bars-wrapper">
				{useSingleProgressBar ? (
					<ProgressBarItem
						testId="progress-bar-single-aggregate"
						progress={getCommonProgress()}
						status={status}
					/>
				) : (
					docs.map((doc, i) => {
						const progressOneDoc = getProgressForOneDoc(doc.completed, i);
						return (
							<ProgressBarItem progress={progressOneDoc} key={i} doc={doc} status={status}>
								{!doc.completed && status !== ProgressStatus.ERROR && (
									<IconButton
										testId="cancel-icon-wrapper"
										label="cancel"
										icon={CrossIcon}
										onClick={() => handleClick(doc)}
									/>
								)}
							</ProgressBarItem>
						);
					})
				)}
			</Box>
		</>
	);
};
